import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { productService } from '../services/api';
import type { Product, ProductCategory } from '../types';
import { formatCentsUSD } from '../lib/money';
import { getProductImageSrc } from '../lib/productImages';
import { useOnlineOrderingEnabled } from '../hooks/usePricingConfig';
import { useProductCategories } from '../hooks/useProductCategories';
import { CATEGORY_LABELS } from '../lib/categories';
import { MORE_LOCATIONS_NOTE } from '../lib/locations';
import { ShoppingCart, MapPin } from 'lucide-react';
import productLifestyle from '../assets/product-lifestyle.webp';
import beachBackdrop from '../assets/beach-backdrop.webp';

interface ProductsPageProps {
  onAddToCart: (product: Product, quantity: number) => void;
}

export function ProductsPage({ onAddToCart }: ProductsPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // null while the store config loads; never flash a buy button in that window.
  const orderingEnabled = useOnlineOrderingEnabled();

  const filter = searchParams.get('category') || 'all';

  // Categories with at least one active product; null while loading. Empty
  // categories never get a filter chip.
  const availableCategories = useProductCategories();

  useEffect(() => {
    setLoading(true);
    const fetcher = filter === 'all' ? productService.getAll() : productService.getByCategory(filter);
    fetcher
      .then(setProducts)
      .catch((err) => {
        console.error(err);
        setProducts([]);
      })
      .finally(() => setLoading(false));
  }, [filter]);

  // Stale link/bookmark to a category with no products (or an invalid one):
  // fall back to showing all products instead of an empty page.
  useEffect(() => {
    if (loading || filter === 'all' || products.length > 0) return;
    if (availableCategories && !(availableCategories as string[]).includes(filter)) {
      setSearchParams({}, { replace: true });
    }
  }, [loading, filter, products, availableCategories, setSearchParams]);

  const categories: ('all' | ProductCategory)[] = ['all', ...(availableCategories ?? [])];

  return (
    <div>
      <section className="relative overflow-hidden">
        <img
          src={productLifestyle}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-coastal-900/70 to-coastal-800/40" />
        <div className="container-max relative z-10 py-16">
          <h1 className="text-4xl font-serif font-bold text-white mb-2 drop-shadow-lg">Our Products</h1>
          <p className="text-white/90 font-semibold drop-shadow">Small-batch, all-natural snacks made by hand</p>
        </div>
      </section>

      {orderingEnabled === false && (
        <section className="py-8">
          <div className="container-max">
            <div className="bg-coastal-50 border-2 border-coastal-200 rounded-lg p-6 sm:p-8 text-center">
              <h2 className="text-2xl font-serif font-bold text-coastal-900 mb-2">Online Ordering Coming Soon</h2>
              <p className="text-gray-700 mb-1">
                For now, you can find our products at{' '}
                <Link to="/locations" className="font-semibold text-coastal-700 underline hover:text-coastal-900">
                  our locations
                </Link>.
              </p>
              <p className="text-gray-700 mb-4">{MORE_LOCATIONS_NOTE}</p>
              <Link to="/locations">
                <Button size="sm" className="inline-flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Where to Find Us
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Filter chips only make sense once there's more than one bucket to
          pick from; with no categorized products, everything is "All". */}
      {categories.length > 1 && (
        <section className="py-8 border-b">
          <div className="container-max">
            <div className="flex flex-wrap gap-3">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    if (cat === 'all') {
                      setSearchParams({});
                    } else {
                      setSearchParams({ category: cat });
                    }
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    filter === cat
                      ? 'bg-coastal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat === 'all' ? 'All' : CATEGORY_LABELS[cat]}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-12">
        <div className="container-max">
          {loading ? (
            <p className="text-center py-12 text-gray-500">Loading products...</p>
          ) : products.length === 0 ? (
            <p className="text-center py-12 text-gray-500">No products found.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => (
                <Card key={product.id} className="flex flex-col h-full">
                  <div className="product-composite relative h-48 rounded-lg overflow-hidden mb-4">
                    <img src={beachBackdrop} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    <img
                      src={getProductImageSrc(product)}
                      alt={product.name}
                      className="absolute inset-0 w-full h-full object-contain z-10 drop-shadow-lg p-3"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-lg">{product.name}</CardTitle>
                    {product.isOrganic && (
                      <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded mt-2 w-fit">
                        All Natural
                      </span>
                    )}
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    <p className="text-sm text-gray-600 mb-4 flex-1">{product.description}</p>
                    <p className="text-2xl font-bold text-coastal-600 mb-4">
                      {formatCentsUSD(product.priceCentsUSD)}
                    </p>
                    {orderingEnabled === null ? (
                      // Store config still loading — hold the button slot so
                      // the card doesn't jump once it resolves.
                      <Button disabled className="w-full invisible" aria-hidden="true">Coming Soon</Button>
                    ) : !orderingEnabled ? (
                      <Button disabled className="w-full">Coming Soon</Button>
                    ) : product.inStock ? (
                      <Button
                        onClick={() => onAddToCart(product, 1)}
                        className="w-full flex items-center justify-center gap-2"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        Add to Cart
                      </Button>
                    ) : (
                      <Button disabled className="w-full">Out of Stock</Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
